import { generateEntityId } from '@modules/helpers';
import { logger } from '@modules/logger';

import { generateAnonymousId } from './helpers';
import { Analytics, AnalyticsUser, GroupEvent, IdentifyEvent, PageEvent, TrackEvent } from './types';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { NEXT_PUBLIC_ANALYTICS_WRITE_KEY, NEXT_PUBLIC_SEGMENT_CDN_DOMAIN } from '@env';
import { getCookie } from 'cookies-next';

/** Gets the value of the ga4 client ID */
function getGaContext(): { sessionId: string | null } {
  return { sessionId: getCookie('_ga4') ?? '' };
}

const platform = 'dash';
const version = 1;

/** Implements Segment analytics tracking for Dashbaord */
export class SegmentAnalytics implements Analytics {
  private analyticsBrowser: AnalyticsBrowser;
  private analyticsInstance: AnalyticsBrowser['instance'] | null = null;

  constructor() {
    this.analyticsBrowser = new AnalyticsBrowser();
  }

  async initialize() {
    const [analytics] = await this.analyticsBrowser.load({
      writeKey: NEXT_PUBLIC_ANALYTICS_WRITE_KEY,
      cdnURL: `https://${NEXT_PUBLIC_SEGMENT_CDN_DOMAIN}`,
    });
    this.analyticsInstance = analytics;
  }

  user(): AnalyticsUser | undefined {
    if (!this.analyticsInstance) return undefined;

    const user = this.analyticsInstance.user();
    return {
      id: () => user.id(),
      anonymousId: () => user.anonymousId(),
    };
  }

  track(e: Partial<TrackEvent>): void {
    try {
      if (!e.name) {
        logger.warn('track() called without name');
        return;
      }

      // add common fields
      const message_id = generateEntityId();
      this.analyticsInstance?.track(
        e.name,
        { platform, version, ...e.properties },
        { context: { message_id, platform, version, ga: getGaContext(), ...e.context } }
      );
    } catch (e) {
      // todo: replace with logging framework
      logger.warn('Error calling Segment track()', { error: e });
    }
  }
  page(e: Partial<PageEvent>): void {
    try {
      e.name ??= 'page_view';
      e.properties ??= {};
      e.properties.host ??= window.location.hostname;
      const message_id = generateEntityId();

      this.analyticsInstance?.page(
        e.name,
        { platform, version, ...e.properties },
        { context: { message_id, version, platform, ga: getGaContext() } }
      );
    } catch (e) {
      // todo: replace with logging framework
      logger.warn('Error calling segment page()', { error: e });
    }
  }
  identify(e: Partial<IdentifyEvent>): void {
    const message_id = generateEntityId();

    if (e.user_id) {
      this.analyticsInstance?.identify(e.user_id, e.traits ?? {}, {
        context: { message_id, version, platform, ga: getGaContext() },
      });
    } else {
      try {
        const anonymousId = generateAnonymousId(e.anonymous_id);
        this.analyticsInstance?.identify(
          { anonymousId, ...(e.traits ?? {}) },
          { context: { message_id, version, platform, ga: getGaContext() } }
        );
      } catch (error) {
        logger.error('Error setting anon ID', { error });
      }
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  group(e: GroupEvent): void {
    logger.warn('group() is not implemented');
  }
  reset(): void {
    this.analyticsInstance?.reset();
  }
}
